"use client";

import type React from "react";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  BarChart,
  Box,
  Settings,
  Home,
  Layers,
  Package,
  Printer,
  ShoppingCart,
  TrendingUp,
  User,
  Warehouse,
  ChevronDown,
  ChevronRight,
  Palette,
  CheckCircle,
  AlertTriangle,
  Menu,
  Search,
  Scissors,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AccountSwitcher } from "@/components/account-switcher";
import { SprayNozzle } from "@/components/icons/spray-nozzle";

export function CollapsibleSidebar() {
  const [expanded, setExpanded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    orders: false,
    batches: false,
    decorationStations: false,
    qualityCheck: false,
    products: false,
    hemming: false,
  });
  const pathname = usePathname();

  // Reset expanded state when navigating
  useEffect(() => {
    setExpanded(false);
    setMobileMenuOpen(false); // Close mobile menu on navigation
  }, [pathname]);

  // Update the toggleSection function to prevent navigation and ensure proper expansion
  const toggleSection = (section: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));

    // Keep the sidebar expanded when toggling sections
    setExpanded(true);
  };

  // Check if the current path is a decoration station
  const isDecorationStation = pathname?.startsWith("/decoration-stations/");

  // Check if the current path is a batches route
  const isBatchesRoute = pathname === "/batches" || pathname === "/batch-search";

  // Check if the current path is a quality check route
  const isQualityCheck =
    pathname?.startsWith("/bins") ||
    pathname?.startsWith("/bin-progress") ||
    pathname?.startsWith("/quality-check/") ||
    pathname === "/reprints" ||
    pathname?.startsWith("/reprints");

  // Check if the current path is a hemming route
  const isHemmingRoute = pathname?.startsWith("/hemming");

  const navItems: Array<{
    href: string;
    label: string;
    icon: any;
    active: boolean;
    badge?: number;
    expandable?: boolean;
    expanded?: boolean;
    section?: string;
    subItems?: Array<{ href: string; label: string; badge?: number }>;
    mobileVisible?: boolean;
  }> = [
    { href: "/", label: "Dashboard", icon: Home, active: pathname === "/", mobileVisible: false },
    {
      href: "/orders",
      label: "Orders",
      icon: ShoppingCart,
      active: pathname === "/orders" || pathname?.startsWith("/orders/"),
      mobileVisible: false,
    },
    {
      href: "/batches",
      label: "Batches",
      icon: Printer,
      active: isBatchesRoute,
      expandable: true,
      expanded: expandedSections.batches,
      section: "batches",
      mobileVisible: false,
      subItems: [
        { href: "/batches", label: "Print Batches" },
        { href: "/batch-search", label: "Search Batches" },
      ],
    },
    {
      href: "/hemming",
      label: "Hemming",
      icon: Scissors,
      active: isHemmingRoute,
      expandable: true,
      expanded: expandedSections.hemming,
      section: "hemming",
      mobileVisible: true,
      subItems: [
        { href: "/hemming/scan-out", label: "Outbound" },
        { href: "/hemming/scan-in", label: "Inbound" },
      ],
    },
    {
      href: "/decoration-stations/placement",
      label: "Decoration Station",
      icon: Palette,
      active: pathname === "/decoration-stations/placement",
      mobileVisible: true,
    },
    {
      href: "/bins",
      label: "Binning",
      icon: Box,
      active: pathname === "/bins",
      mobileVisible: true,
    },
    {
      href: "/quality-check/scan",
      label: "QC Scan",
      icon: AlertTriangle,
      active: pathname === "/quality-check/scan",
      mobileVisible: true,
    },

    // {
    //   href: "/pre-treatment",
    //   label: "Pre-Treatment",
    //   icon: SprayNozzle,
    //   active: pathname === "/pre-treatment",
    //   mobileVisible: true,
    // },
    // {
    //   href: "/decoration-stations",
    //   label: "Decoration Stations",
    //   icon: Palette,
    //   active: isDecorationStation,
    //   expandable: true,
    //   expanded: expandedSections.decorationStations,
    //   section: "decorationStations",
    //   mobileVisible: true,
    //   subItems: [
    //     // { href: "/decoration-stations/dtg", label: "DTG" },
    //     // { href: "/decoration-stations/dtf", label: "DTF" },
    //     // { href: "/decoration-stations/engraving", label: "Engraving" },
    //     // { href: "/decoration-stations/embroidery", label: "Embroidery" },
    //     { href: "/decoration-stations/placement", label: "Placement" },
    //     // { href: "/decoration-stations/assembly", label: "Assembly" },
    //   ],
    // },
    {
      href: "/quality-check",
      label: "Quality Check",
      icon: CheckCircle,
      active: isQualityCheck,
      expandable: true,
      expanded: expandedSections.qualityCheck,
      section: "qualityCheck",
      mobileVisible: false,
      subItems: [
        { href: "/bin-progress", label: "Bin Progress" },
        { href: "/quality-check/reprints", label: "Reprints" },
        { href: "/quality-check/misprint-reasons", label: "Misprint Reasons" },
      ],
    },
    {
      href: "/products",
      label: "Products",
      icon: Package,
      active: pathname?.startsWith("/products"),
      expandable: true,
      expanded: expandedSections.products,
      section: "products",
      mobileVisible: false,
      subItems: [
        { href: "/products", label: "Products List" },
        // { href: "/products/blank-skus", label: "Blank SKUs" },
        // { href: "/products/designs", label: "Design Files" },
        { href: "/products/design-profiles", label: "Design Profiles" },
        { href: "/products/placement-skus", label: "Placement SKUs" },
        // { href: "/products/print-settings", label: "Print Settings" },
      ],
    },
    // {
    //   href: "/inventory",
    //   label: "Inventory",
    //   icon: Warehouse,
    //   active: pathname === "/inventory",
    //   mobileVisible: false,
    // },
    {
      href: "/productivity",
      label: "Productivity",
      icon: TrendingUp,
      active: pathname === "/productivity",
      mobileVisible: false,
    },
    // {
    //   href: "/reports",
    //   label: "Reports",
    //   icon: BarChart,
    //   active: pathname === "/reports",
    //   mobileVisible: false,
    // },
    {
      href: "/settings",
      label: "Settings",
      icon: Settings,
      active: pathname === "/settings",
      mobileVisible: false,
    },
  ];

  return (
    <>
      {/* Hamburger Button (Mobile only) */}
      {!mobileMenuOpen && (
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="fixed top-4 left-4 z-[100] lg:hidden p-2"
          aria-label="Toggle menu"
        >
          <Menu className="h-6 w-6 text-slate-700" />
        </button>
      )}

      {/* Backdrop (Mobile only) */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full z-[70] flex flex-col border-r bg-white shadow-md transition-all duration-300 ease-in-out
          ${expanded || mobileMenuOpen ? "w-64" : "w-16"}
          lg:translate-x-0 ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => !mobileMenuOpen && setExpanded(false)}
      >
        <div className="flex h-16 items-center justify-start px-3 border-b">
          <Link href="/" className="flex items-center gap-3">
            <div className="h-8 w-8 rounded overflow-hidden flex-shrink-0">
              <Image
                src="/images/podhero-logo.png"
                alt="PODHero Logo"
                width={32}
                height={32}
                className="object-cover"
              />
            </div>
            {(expanded || mobileMenuOpen) && <span className="text-xl font-semibold">PODHero</span>}
          </Link>
        </div>
        <div className="flex-1 overflow-auto py-2">
          <nav className="grid items-start px-2 text-sm font-medium">
            {navItems.map((item) => (
              <div
                key={item.href}
                className={item.mobileVisible === false ? "hidden lg:block" : ""}
              >
                {item.expandable ? (
                  <button
                    onClick={(e) => item.section && toggleSection(item.section, e)}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 my-1 transition-colors w-full text-left ${
                      item.active
                        ? "bg-slate-100 text-slate-900"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    }`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {(expanded || mobileMenuOpen) && (
                      <>
                        <span className="truncate">{item.label}</span>
                        {item.badge && (
                          <Badge className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs text-slate-500">
                            {item.badge}
                          </Badge>
                        )}
                        {item.expandable && (
                          <div className="ml-auto">
                            {item.expanded ? (
                              <ChevronDown className="h-4 w-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-slate-500" />
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </button>
                ) : (
                  <Link
                    href={item.href}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 my-1 transition-colors ${
                      item.active
                        ? "bg-slate-100 text-slate-900"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    }`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {(expanded || mobileMenuOpen) && (
                      <>
                        <span className="truncate">{item.label}</span>
                        {item.badge && (
                          <Badge className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs text-slate-500">
                            {item.badge}
                          </Badge>
                        )}
                      </>
                    )}
                  </Link>
                )}
                {item.expandable && item.expanded && (expanded || mobileMenuOpen) && (
                  <div className="ml-9 mt-1 space-y-1">
                    {item.subItems?.map(
                      (subItem: { href: string; label: string; badge?: number }) => (
                        <Link
                          key={subItem.href}
                          href={subItem.href}
                          className={`flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                            pathname === subItem.href
                              ? "bg-slate-100 text-slate-900"
                              : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                          }`}
                        >
                          <span>{subItem.label}</span>
                          {subItem.badge && (
                            <Badge className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs text-slate-500">
                              {subItem.badge}
                            </Badge>
                          )}
                        </Link>
                      ),
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
        <div className="mt-auto p-2 border-t">
          <AccountSwitcher expanded={expanded || mobileMenuOpen} />
        </div>
      </div>
    </>
  );
}
